import { useMemo, useState } from 'react';
import { SUPPORTED_CLAIMS, TYP, type BindingPayload, type StatePayload } from '@pryme/protocol';
import { Alert, Badge, Spinner, useAsyncAction } from '@pryme/ui';
import { ErrorBox } from '../components/ErrorBox';
import { IdentityStatusBadge, IdentityStatusLegend } from '../components/IdentityStatus';
import { JwsCard } from '../components/JwsCard';
import { SecretKeyInput } from '../components/SecretKeyInput';
import { useServiceOutlet } from '../components/ServiceLayout';
import { TraceView } from '../components/TraceView';
import { identityStatusFromBinding, inspectJws, issuerKeysOf, matchSecret, prymeKeysOf, signedServiceRequest, type SignedTrace } from '../lib/signing';

type Endpoint = '/v1/playground/echo' | '/v1/verify' | '/v1/verify/refresh';

export function Playground() {
  const { service: s } = useServiceOutlet();
  const [secret, setSecret] = useState('');
  const [endpoint, setEndpoint] = useState<Endpoint>('/v1/playground/echo');
  const [subject, setSubject] = useState('');
  const [claims, setClaims] = useState<string[]>(s.claimConfig.requested);
  const [audience, setAudience] = useState(s.id);
  const [customBody, setCustomBody] = useState<string | null>(null);
  const [trace, setTrace] = useState<SignedTrace | null>(null);
  const [lastNonce, setLastNonce] = useState<string | null>(null);
  const send = useAsyncAction();
  const match = matchSecret(secret, s.keys);
  // Exactly what a backend on this secret key trusts: its own generation's verification key.
  const prymeKeys = prymeKeysOf(s, match.ok ? match.key : undefined);

  const generatedBody = useMemo(() => {
    if (endpoint === '/v1/playground/echo') return { hello: 'pryme', sentAt: new Date().toISOString().slice(0, 19) + 'Z' };
    return { subject: subject.trim(), requestedClaims: claims, ...(audience ? { audience } : {}) };
  }, [endpoint, subject, claims, audience]);
  const bodyText = customBody ?? JSON.stringify(generatedBody, null, 2);
  let parsed: unknown = null;
  let bodyError: string | null = null;
  try {
    parsed = JSON.parse(bodyText);
  } catch (e) {
    bodyError = (e as Error).message;
  }

  const run = async (replayNonce?: string) => {
    if (!match.ok || bodyError) return;
    const r = await send.run(() =>
      signedServiceRequest({ serviceId: s.id, keyId: match.key.id, secretKey: match.signingSecret, audience: s.apiAudience, path: endpoint, body: parsed, prymeKeys, nonce: replayNonce }),
    );
    if (r) {
      setTrace(r);
      setLastNonce(r.requestHeaders['pryme-nonce'] ?? null);
    }
  };

  const resp = trace?.ok ? (trace.responseJson as { binding?: string; state?: string; environment?: string } | null) : null;
  const binding = inspectJws<BindingPayload>(resp?.binding, issuerKeysOf(s), TYP.binding);
  const bp = binding?.verified ? binding.decoded?.payload : undefined;
  const state = inspectJws<StatePayload>(resp?.state, bp ? [{ keyId: bp.pairwise_kid, publicKey: bp.pairwise_key }] : [], TYP.state);
  const sp = state?.verified ? state.decoded?.payload : undefined;

  return (
    <>
      <section className="p-card" aria-labelledby="pg-h">
        <h2 id="pg-h">API playground</h2>
        <p className="p-small p-muted">
          Build a real request signed with your signing key in your browser, send it to Pryme, and inspect the canonical signature base, headers, response and every signature check. Use
          sandbox credentials. Your secret key never leaves this tab.
        </p>
        <SecretKeyInput value={secret} onChange={setSecret} keys={s.keys} />

        <div className="p-grid">
          <div className="p-field">
            <label htmlFor="ep">Endpoint</label>
            <select id="ep" className="p-select" value={endpoint} onChange={(e) => { setEndpoint(e.target.value as Endpoint); setCustomBody(null); }}>
              <option value="/v1/playground/echo">POST /v1/playground/echo · what Pryme verified</option>
              <option value="/v1/verify">POST /v1/verify · signed claim state for a subject</option>
              <option value="/v1/verify/refresh">POST /v1/verify/refresh</option>
            </select>
          </div>
          {endpoint !== '/v1/playground/echo' && (
            <div className="p-field">
              <label htmlFor="subj">Pairwise subject</label>
              <input id="subj" className="p-input mono" value={subject} onChange={(e) => { setSubject(e.target.value); setCustomBody(null); }} placeholder="pks_…" />
              <span className="p-hint">From <code>identity.subject</code> after a login, or from a mandate binding (Mandate playground).</span>
            </div>
          )}
        </div>
        {endpoint !== '/v1/playground/echo' && (
          <>
            <fieldset className="p-field" style={{ border: 0, padding: 0 }}>
              <legend style={{ fontWeight: 600, fontSize: '0.92rem' }}>Requested claims</legend>
              <div className="p-row">
                {SUPPORTED_CLAIMS.map((c) => (
                  <label key={c} className="p-check" style={{ padding: 0 }}>
                    <input type="checkbox" checked={claims.includes(c)} onChange={() => { setCustomBody(null); setClaims((x) => (x.includes(c) ? x.filter((y) => y !== c) : [...x, c])); }} />
                    <code>{c}</code>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="p-field">
              <label htmlFor="aud">Body audience (optional)</label>
              <input id="aud" className="p-input mono" value={audience} onChange={(e) => { setAudience(e.target.value); setCustomBody(null); }} />
              <span className="p-hint">Must equal your service ID; try another value to see <code>wrong_audience</code>.</span>
            </div>
          </>
        )}
        <div className="p-field">
          <label htmlFor="body">Request body (JSON, editable)</label>
          <textarea id="body" className="p-textarea" rows={6} value={bodyText} onChange={(e) => setCustomBody(e.target.value)} aria-invalid={!!bodyError} />
          {bodyError && <span className="p-hint" style={{ color: 'var(--p-bad)' }}>Invalid JSON: {bodyError}</span>}
          {customBody !== null && <button type="button" className="p-btn ghost small" onClick={() => setCustomBody(null)}>Reset to generated body</button>}
        </div>
        <div className="p-row">
          <button className="p-btn" disabled={!match.ok || !!bodyError || send.busy} onClick={() => run()}>
            {send.busy ? <Spinner label="Signing & sending…" /> : 'Sign & send'}
          </button>
          <button className="p-btn secondary" disabled={!match.ok || !lastNonce || send.busy} onClick={() => run(lastNonce!)} title="Re-send with the previous nonce">
            Replay last nonce
          </button>
        </div>
        <p className="p-small p-muted">“Replay last nonce” re-signs a request with the nonce you already used: Pryme must reject it with <code>replay_detected</code> (visible in Live events).</p>
        <ErrorBox error={send.error} />
      </section>

      {trace && (
        <section className="p-card" aria-labelledby="tr-h">
          <h2 id="tr-h">Result</h2>
          {!trace.ok && (
            <ErrorBox error={Object.assign(new Error((trace.responseJson as { error?: { message?: string } })?.error?.message ?? ''), { code: (trace.responseJson as { error?: { code?: string } })?.error?.code })} />
          )}
          <TraceView trace={trace} />
        </section>
      )}

      {(binding || state) && (
        <section className="p-card" aria-labelledby="jws-h">
          <h2 id="jws-h">Signed proofs</h2>
          <IdentityStatusBadge status={identityStatusFromBinding(bp)} />
          {sp && (
            <div className="p-table-wrap" style={{ marginTop: '0.75rem' }}>
              <table className="p-table">
                <caption className="p-small p-muted" style={{ textAlign: 'left' }}>Disclosed derived claims (valid until {new Date(sp.exp * 1000).toLocaleTimeString()})</caption>
                <thead><tr><th>Claim</th><th>Value</th><th>Assurance</th></tr></thead>
                <tbody>
                  {Object.entries(sp.claims).map(([k, c]) => (
                    <tr key={k}>
                      <td><code>{k}</code></td>
                      <td><code>{String(c.value)}</code></td>
                      <td className="p-small">
                        <Badge tone={c.assurance.environment === 'production' ? 'ok' : 'sandbox'}>{c.assurance.environment}</Badge> {c.assurance.category} · {c.assurance.level} · {c.assurance.provider}/{c.assurance.scheme}
                      </td>
                    </tr>
                  ))}
                  {Object.entries(sp.withheld ?? {}).map(([k, r]) => (
                    <tr key={k}><td><code>{k}</code></td><td><Badge tone="neutral">{r}</Badge></td><td className="p-small p-muted">Withheld: the SDK predicate returns false and claimStatus() explains why</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-grid" style={{ marginTop: '1rem' }}>
            <JwsCard title={`Binding (${TYP.binding})`} inspection={binding} keyLabel="Pryme attestation key" />
            <JwsCard title="State (pryme-state-v1)" inspection={state} keyLabel="pairwise key" />
          </div>
          <p className="p-small p-muted">
            Chain: Pryme's attestation key verifies the binding; the binding certifies the pairwise public key for <em>your</em> service; the pairwise key verifies the state. Pryme's verification key in your secret checks the response itself, not the identity. The subject is pairwise, so other services see a different one.
          </p>
        </section>
      )}

      <section className="p-card" aria-labelledby="leg-h">
        <h2 id="leg-h">Identity status vocabulary</h2>
        <IdentityStatusLegend />
        {s.environment === 'sandbox' && <Alert tone="warn">Sandbox: every person here is a test identity. Do not present them as real MitID-verified people.</Alert>}
      </section>
    </>
  );
}
